import type { AudioTranscriptionRequest, AudioTranscriptionResponse } from '../types/voice';

const QA_API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

export class ApiService {
  private static isAuthDisabled(): boolean {
    return import.meta.env.VITE_DISABLE_AUTH === 'true';
  }

  private static getAuthToken(): string | null {
    return localStorage.getItem('access_token');
  }

  private static async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getAuthToken();
    const authDisabled = this.isAuthDisabled();
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Only add auth header if auth is not disabled and token exists
    if (!authDisabled && token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${QA_API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      if (response.status === 401 && !authDisabled) {
        // Token expired or invalid, clear local storage (only if auth is enabled)
        localStorage.removeItem('access_token');
        localStorage.removeItem('token_expiry');
        throw new Error('Authentication required');
      }
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  static async get<T = any>(endpoint: string): Promise<T> {
    return this.makeRequest<T>(endpoint, { method: 'GET' });
  }

  static async post<T = any>(endpoint: string, body: any): Promise<T> {
    return this.makeRequest<T>(endpoint, { method: 'POST', body: JSON.stringify(body) });
  }

  static async postBinaryOrJson(endpoint: string, body: any): Promise<Blob | any> {
    const token = this.getAuthToken();
    const authDisabled = this.isAuthDisabled();
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (!authDisabled && token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${QA_API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      if (response.status === 401 && !authDisabled) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('token_expiry');
        throw new Error('Authentication required');
      }
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const contentType = response.headers.get('Content-Type') || '';
    if (contentType.startsWith('audio/')) {
      return await response.blob();
    }
    return await response.json();
  }

  private static async makeFormRequest<T>(
    endpoint: string,
    formData: FormData,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getAuthToken();
    const authDisabled = this.isAuthDisabled();
    
    const headers: HeadersInit = {
      ...options.headers,
    };

    // Only add auth header if auth is not disabled and token exists
    if (!authDisabled && token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${QA_API_BASE_URL}${endpoint}`, {
      ...options,
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      if (response.status === 401 && !authDisabled) {
        // Token expired or invalid, clear local storage (only if auth is enabled)
        localStorage.removeItem('access_token');
        localStorage.removeItem('token_expiry');
        throw new Error('Authentication required');
      }
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // Get API status
  static async getStatus(): Promise<{ status: string; timestamp: string }> {
    return this.makeRequest('/api/status');
  }

  // Submit a question to the QA service
  static async submitQuestion(question: string): Promise<{
    success: boolean;
    data?: {
      answer: string;
      sources?: string[];
      confidence?: number;
    };
    error?: {
      message: string;
      code?: string;
    };
  }> {
    return this.makeRequest('/api/ask', {
      method: 'POST',
      body: JSON.stringify({ question }),
    });
  }

  // Get chat history (if implemented)
  static async getChatHistory(): Promise<{
    success: boolean;
    data?: Array<{
      id: string;
      question: string;
      answer: string;
      timestamp: string;
    }>;
    error?: {
      message: string;
      code?: string;
    };
  }> {
    return this.makeRequest('/api/history');
  }

  // Test authenticated endpoint
  static async testAuth(): Promise<{ authenticated: boolean; user?: any }> {
    return this.makeRequest('/api/auth/test');
  }

  // Transcribe audio using Cloudflare Whisper
  static async transcribeAudio(request: AudioTranscriptionRequest): Promise<AudioTranscriptionResponse> {
    try {
      const formData = new FormData();
      formData.append('audio', request.audio);
      
      if (request.language) {
        formData.append('language', request.language);
      }
      
      if (request.prompt) {
        formData.append('prompt', request.prompt);
      }

      const response = await this.makeFormRequest<AudioTranscriptionResponse>('/api/transcribe', formData);
      
      return {
        success: true,
        text: response.text || '',
        language: response.language,
        confidence: response.confidence,
        duration: response.duration,
        ...response
      };
    } catch (error) {
      console.error('Transcription error:', error);
      
      return {
        success: false,
        text: '',
        error: error instanceof Error ? error.message : 'Unknown transcription error'
      };
    }
  }
}